"use client";
import React, { useEffect, useRef, useState } from 'react';
import AppImage from '@/components/ui/AppImage';

const stats = [
{ value: 8, suffix: '+', label: 'Years of Excellence', desc: 'Continuously serving Qatar\'s workforce needs since 2004' },
{ value: 1500, suffix: '+', label: 'Active Workforce', desc: 'Workers deployed and managed across Qatar right now' },
{ value: 50000, suffix: '+', label: 'Pre-Qualified Talent', desc: 'Ready-to-deploy professionals across 15+ source countries' },
{ value: 5, suffix: 'M+', label: 'Man Hours Delivered', desc: 'Safe and productive work hours across all sectors' }];


function useCounter(target: number, duration: number, start: boolean) {
  const [count, setCount] = useState(0);
  useEffect(() => {
    if (!start) return;
    let startTime: number | null = null;
    const step = (timestamp: number) => {
      if (!startTime) startTime = timestamp;
      const progress = Math.min((timestamp - startTime) / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setCount(Math.floor(eased * target));
      if (progress < 1) requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
  }, [target, duration, start]);
  return count;
}

function Counter({ value, suffix, label, desc, start }: {value: number;suffix: string;label: string;desc: string;start: boolean;}) {
  const count = useCounter(value, 2000, start);
  const display = value >= 1000 ? count.toLocaleString() : count;
  return (
    <div
      className="p-6 rounded-2xl transition-all duration-300 hover:border-cyan/30 group"
      style={{
        background: 'rgba(10,36,80,0.4)',
        border: '1px solid rgba(255,255,255,0.06)'
      }}
      onMouseEnter={(e) => e.currentTarget.style.borderColor = 'rgba(0,194,255,0.25)'}
      onMouseLeave={(e) => e.currentTarget.style.borderColor = 'rgba(255,255,255,0.06)'}>
      
      <div
        className="counter-value text-4xl lg:text-5xl font-extrabold gradient-text mb-2"
        style={{ fontFamily: 'Syne, sans-serif' }}>
        
        {display}{suffix}
      </div>
      <div className="text-fg font-bold text-base mb-2" style={{ fontFamily: 'Syne, sans-serif' }}>
        {label}
      </div>
      <p className="text-muted text-sm leading-relaxed" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif', fontWeight: 300 }}>
        {desc}
      </p>
    </div>);

}

export default function StatsSection() {
  const ref = useRef<HTMLDivElement>(null);
  const [started, setStarted] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !started) {
            setStarted(true);
            entry.target.querySelectorAll('.reveal-hidden').forEach((el) => {
              el.classList.add('revealed');
            });
          }
        });
      },
      { threshold: 0.2 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [started]);

  return (
    <section id="stats" ref={ref} className="relative overflow-hidden" style={{ background: '#071e3d' }}>
      <div className="grid grid-cols-1 lg:grid-cols-2 min-h-[600px]">
        {/* Left: Photo */}
        <div className="relative h-72 lg:h-auto">
          <AppImage
            src="https://img.rocket.new/generatedImages/rocket_gen_img_169494f18-1772906007698.png"
            alt="Workers in safety gear at an industrial construction site in Qatar"
            fill
            className="object-cover"
            sizes="(max-width: 1024px) 100vw, 50vw" />
          
          <div
            className="absolute inset-0"
            style={{ background: 'linear-gradient(135deg, rgba(26,86,176,0.4) 0%, rgba(0,194,255,0.15) 100%)' }} />
          
          {/* Overlay text */}
          <div className="absolute bottom-8 left-8 right-8">
            <div
              className="glass px-6 py-4 rounded-2xl"
              style={{ border: '1px solid rgba(0,194,255,0.2)' }}>
              
              <p className="text-fg font-bold text-lg" style={{ fontFamily: 'Syne, sans-serif' }}>
                "Qatar's workforce backbone — trusted by the nation's biggest names."
              </p>
              <p className="text-muted text-sm mt-2">— Fornax Global Leadership</p>
            </div>
          </div>
        </div>

        {/* Right: Stats */}
        <div className="py-20 px-8 lg:px-16 flex flex-col justify-center" style={{ background: '#020d1f' }}>
          <div className="reveal-hidden mb-12">
            <p className="eyebrow mb-4">OUR TRACK RECORD</p>
            <h2
              className="text-3xl lg:text-4xl font-extrabold text-fg leading-[1.15]"
              style={{ fontFamily: 'Syne, sans-serif' }}>
              
              Numbers That{' '}
              <span className="gradient-text">Speak for Themselves</span>
            </h2>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {stats.map((s, i) =>
            <div key={s.label} className={`reveal-hidden stagger-${i + 1}`}>
                <Counter {...s} start={started} />
              </div>
            )}
          </div>
        </div>
      </div>
    </section>);

}